import React, {PropsWithChildren, ReactElement, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Trans, useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import * as utc from 'dayjs/plugin/utc';
import {API} from '@chekin/api';
import {ShortReservation, Housing, ORIGINS, QUERY_CACHE_KEYS} from '@chekin/core';
import {useChekinSDK} from '../ChekinSDK';
import {environments} from 'configs';
import {useCustomTheme} from '../customTheme';
import {useReservationFetchListener} from 'hooks/embedded';
import {useFetchHousing} from 'hooks';
import {useIpDetails} from '../ipDetails';
import ChekinProSettings from 'ChekinProSettings';
import {Analytics} from '@chekin/shared';

const IS_SDK_MODE = environments.VITE_APP_IS_SDK;

type ContextProps = {
  data?: ShortReservation;
  housing?: Housing;
  error?: Error | null;
  reservationError?: string | ReactElement;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  housingId?: string;
  reservationId?: string;
  isBiometricMatchForAll?: boolean;
  isSuccessLoadedHousing?: boolean;
  hasAvantio?: boolean;
  allowEditingGuestNumber?: boolean;
};

enum GUESTAPP_V1_DOMAINS {
  staging = 'new-chekin.stg.chekin.com',
  production = 'guestapp.chekin.com',
}

const ReservationContext = React.createContext<ContextProps>({
  data: undefined,
  housing: undefined,
  isLoading: false,
  isFetching: false,
});

function ReservationProvider({children}: Readonly<PropsWithChildren>) {
  const {foundReservationId, isSuccessSDK} = useChekinSDK();
  const externalId = ChekinProSettings.externalId;
  const {ipDetails} = useIpDetails();
  const {t} = useTranslation();
  const {isTemplateInitialized} = useCustomTheme();
  dayjs.extend(utc.default);
  dayjs.extend(isBetween);
  const enabled =
    isTemplateInitialized && Boolean(foundReservationId || externalId) && isSuccessSDK;

  const {
    data: reservation,
    error,
    isLoading,
    isFetching,
    isError,
    status,
  } = useQuery<ShortReservation, Error & {status?: number}>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.reservation],
    queryFn: () =>
      API.reservation.getOne({id: foundReservationId || '', externalId: externalId}),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });
  useReservationFetchListener({status, reservation});

  useEffect(() => {
    if (
      reservation &&
      !IS_SDK_MODE &&
      (reservation?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.staging) ||
        reservation?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.production))
    ) {
      window.location.href = reservation?.signup_form_link;
    }
  }, [reservation]);

  useEffect(() => {
    // TODO This code is for testing in STG, it will be removed
    if (reservation?.analytics_tools_enabled && ipDetails) {
      Analytics.initialize();
      Analytics.identify(ipDetails?.ip);
      Analytics.addEventProperties({
        'Reservation id': reservation?.id,
      });
    }
  }, [ipDetails, reservation?.analytics_tools_enabled, reservation?.id]);

  const housingId = reservation?.housing_id;
  const reservationError = React.useMemo(() => {
    if (IS_SDK_MODE && error) {
      if (error?.message.includes('Found many active reservations')) {
        return (
          <Trans i18nKey="to_many_reservations_external_id">
            There are many reservations associated with the <strong>EXTERNAL ID</strong>{' '}
            provided in the implementation, make sure there is only one reservation
            associated with the <strong>EXTERNAL ID</strong>. <br /> If the problem
            persist, please contact support.
          </Trans>
        );
      } else if (error?.status === 404) {
        return t('reservation_not_found');
      } else {
        return error?.message;
      }
    }

    return error?.message;
  }, [error, t]);

  const {data: housing, isSuccess: isSuccessLoadedHousing} = useFetchHousing(housingId, {
    initialData: {} as Housing,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: Boolean(housingId),
  });

  const isBiometricMatchForAll = housing?.is_biometric_match_for_all_enabled;
  const hasAvantio = reservation?.origin === ORIGINS.avantio;
  const allowEditingGuestNumber = housing?.allow_guest_number_modification;
  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: reservation,
      reservationId: reservation?.id || '',
      housing,
      housingId,
      error,
      isLoading,
      isFetching,
      isError,
      reservationError,
      isBiometricMatchForAll,
      isSuccessLoadedHousing,
      hasAvantio,
      allowEditingGuestNumber,
    }),
    [
      error,
      housing,
      housingId,
      reservationError,
      isBiometricMatchForAll,
      isFetching,
      isLoading,
      isError,
      reservation,
      isSuccessLoadedHousing,
      hasAvantio,
      allowEditingGuestNumber,
    ],
  );

  return <ReservationContext.Provider value={defaultProps} children={children} />;
}

export {ReservationContext, ReservationProvider};
